import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {
  ActivityPeriodReport,
  ActivityPeriodReportArrayValidator,
  ActivityPeriodReportEntity
} from "./activity-period-report";
import {activityPeriodStatus, ActivityPeriodStatus, toActivityPeriodStatus} from "./activity-period-status";
import {
  ActivityPeriodStatusTime,
  ActivityPeriodStatusTimeArrayValidator,
  ActivityPeriodStatusTimeEntity
} from "./activity-period-status-time";
import {ReportTimeType, toReportTimeType} from "./report-time-type";
import {getDatabaseCodeTransformer, getTransportCategoryTransformer} from "./transfomers/transformers";
import {TransportCategorizable, TransportCategory} from "./transport-category";
import {
  ObjectDocumentAssoc,
  ObjectDocumentAssocArrayValidator,
  ObjectDocumentAssocEntity
} from "./object-document-assoc";
import { Entity } from "./entity";

interface ActivityPeriodInterface {
  id: string,
  operatorId: string,
  operatorUnitId: string
  truckCompany?: string
  regionId: string
  category: string
  type: string
  currentStatus: string
  calendarIndicator: number
  yearIndicator: number
  fromDttm: number
  toDttm: number
  lastModifiedDttm: number
  createDttm: number
  activityPeriodReportList?: ActivityPeriodReport[]
  activityPeriodStatusTimesList?: ActivityPeriodStatusTime[]
  truckCompanyName?: string
  operatorName?: string
  operatorAbbr?: string
  operatorUnitName?: string
  cost?: number
  ticketQty?: number
  qty?: number
  holder?: boolean,
  adminValidationCount?: number
  remainingAdminValidationCount?: number
  fieldValidationCount?: number
  businessValidationCount?: number
  remainingBusinessValidationCount?: number
  disputeCount?: number
  supplementals?: ObjectDocumentAssoc[]
  database?: string
  activityPeriods?: ActivityPeriodInterface[]
  totalCost?: number
  totalTicketQty?: number
  totalQty?: number
  totalAdminValidationCount?: number
  totalRemainingAdminValidationCount?: number
  totalFieldValidationCount?: number
  totalBusinessValidationCount?: number
  totalRemainingBusinessValidationCount?: number
  totalDisputeCount?: number
  lowestStatus?: string
  personalResponsibilityCounts?: ActivityPeriodResponsibilityCount[]
}

export type ActivityPeriodSummarySource = 'ROLLUP' | 'LIVE';

export type ActivityPeriodResponsibilityCount = {
  action: string;
  remainingCount: number;
  openCount: number;
  inProgressCount: number;
  completedCount: number;
  supersededCount: number;
  cancelledCount: number;
  totalCount: number;
};

export type ActivityPeriodRollup = {
  activityPeriodId: string;
  ticketCount?: number;
  totalQty?: number;
  holderAvailable?: boolean;
  adminValidationCount?: number;
  fieldValidationCount?: number;
  businessValidationCount?: number;
  disputeCount?: number;
  codingCount?: number;
  codingDisputeCount?: number;
  dirty: boolean;
  dirtyDttm?: number;
  refreshedDttm?: number;
};

export type ActivityPeriodRateModelSummary = {
  activityPeriodId: string;
  rateModelId: string;
  costedTicketQty: number;
  /** Omitted or null when cost masking applies. */
  totalCost?: number | null;
  rateModelName?: string;
  rateModelType?: string;
  companyId?: string;
  companyName?: string;
  companyType?: string;
  regionId?: string;
};

export type ActivityPeriodSummary = {
  totals: ActivityPeriodRollup;
  rateModels: ActivityPeriodRateModelSummary[];
  source: ActivityPeriodSummarySource;
  personalResponsibilityCounts: ActivityPeriodResponsibilityCount[];
};

export const ActivityPeriodValidator: t.Type<ActivityPeriodInterface> = t.recursion('ActivityPeriod', () =>
  t.intersection([
    t.type({
      id: t.string,
      operatorId: t.string,
      operatorUnitId: t.string,
      regionId: t.string,
      category: t.string,
      type: t.string,
      currentStatus: t.string,
      calendarIndicator: t.number,
      yearIndicator: t.number,
      fromDttm: t.number,
      toDttm: t.number,
      lastModifiedDttm: t.number,
      createDttm: t.number,
    }),
    t.partial({
      activityPeriodReportList: ActivityPeriodReportArrayValidator,
      activityPeriodStatusTimesList: ActivityPeriodStatusTimeArrayValidator,
      truckCompanyId: t.string,
      truckCompanyName: t.string,
      operatorName: t.string,
      operatorAbbr: t.string,
      operatorUnitName: t.string,
      cost: t.number,
      ticketQty: t.number,
      qty: t.number,
      holder: t.boolean,
      adminValidationCount: t.number,
      remainingAdminValidationCount: t.number,
      fieldValidationCount: t.number,
      businessValidationCount: t.number,
      remainingBusinessValidationCount: t.number,
      disputeCount: t.number,
      supplementals: ObjectDocumentAssocArrayValidator,
      activityPeriods: t.array(ActivityPeriodValidator),
      database: t.string,
      totalCost: t.number,
      totalTicketQty: t.number,
      totalQty: t.number,
      totalAdminValidationCount: t.number,
      totalRemainingAdminValidationCount: t.number,
      totalFieldValidationCount: t.number,
      totalBusinessValidationCount: t.number,
      totalRemainingBusinessValidationCount: t.number,
      totalDisputeCount: t.number,
      lowestStatus: t.string,
      personalResponsibilityCounts: t.array(t.type({
        action: t.string,
        remainingCount: t.number,
        openCount: t.number,
        inProgressCount: t.number,
        completedCount: t.number,
        supersededCount: t.number,
        cancelledCount: t.number,
        totalCount: t.number,
      })),
    }),
  ])
)

export const ActivityPeriodArrayValidator = t.array(ActivityPeriodValidator);

export type ActivityPeriod = t.TypeOf<typeof ActivityPeriodValidator>;
export type ActivityPeriodArray = t.TypeOf<typeof ActivityPeriodArrayValidator>;

@Exclude()
export class ActivityPeriodEntity implements Entity, TransportCategorizable {
  @Expose()
  id!: string;

  @Expose()
  operatorId!: string;

  @Expose()
  operatorUnitId?: string;

  @Expose()
  operatorUnitName?: string;

  @Expose()
  truckCompanyId!: string;

  @Expose()
  regionId!: string;

  @Transform(getTransportCategoryTransformer())
  @Expose()
  category!: TransportCategory;

  @Transform(getDatabaseCodeTransformer(toReportTimeType))
  @Expose()
  type!: ReportTimeType;

  @Transform(getDatabaseCodeTransformer(toActivityPeriodStatus))
  @Expose()
  currentStatus!: ActivityPeriodStatus;

  @Expose()
  calendarIndicator!: number;

  @Expose()
  yearIndicator!: number;

  @Expose()
  fromDttm!: number;

  @Expose()
  toDttm!: number;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose({name: 'activityPeriodReportList'})
  @Type(() => ActivityPeriodReportEntity)
  activityPeriodReports: ActivityPeriodReportEntity[] = [];

  @Expose({name: 'activityPeriodStatusTimesList'})
  @Type(() => ActivityPeriodStatusTimeEntity)
  activityPeriodStatusTimes: ActivityPeriodStatusTimeEntity[] = [];

  @Expose()
  @Type(() => ObjectDocumentAssocEntity)
  supplementals?: ObjectDocumentAssocEntity[]

  @Expose()
  truckCompanyName?: string;

  @Expose()
  operatorName?: string;

  @Expose()
  operatorAbbr?: string;

  @Expose()
  cost = 0;

  @Expose()
  ticketQty = 0;

  @Expose()
  qty = 0;

  @Expose()
  holder = false;

  @Expose()
  adminValidationCount = 0;

  @Expose()
  remainingAdminValidationCount = 0;

  @Expose()
  fieldValidationCount = 0;

  @Expose()
  businessValidationCount = 0;

  @Expose()
  remainingBusinessValidationCount = 0;

  @Expose()
  disputeCount = 0;

  @Expose()
  personalResponsibilityCounts: ActivityPeriodResponsibilityCount[] = [];

  @Expose()
  database?: string;

  @Type(() => ActivityPeriodEntity)
  @Expose()
  activityPeriods?: ActivityPeriodEntity[]

  @Expose()
  totalCost = 0;

  @Expose()
  totalTicketQty = 0;

  @Expose()
  totalQty = 0;

  @Expose()
  totalAdminValidationCount = 0;

  @Expose()
  totalRemainingAdminValidationCount = 0;

  @Expose()
  totalFieldValidationCount = 0;

  @Expose()
  totalBusinessValidationCount = 0;

  @Expose()
  totalRemainingBusinessValidationCount = 0;

  @Expose()
  totalDisputeCount = 0;

  @Transform(
    getDatabaseCodeTransformer(
      toActivityPeriodStatus,
      activityPeriodStatus.raw
    )
  )
  @Expose()
  lowestStatus: ActivityPeriodStatus = activityPeriodStatus.raw;

  readonly entityType = "ACTIVITYPERIOD";

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for ActivityPeriodEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ActivityPeriod): ActivityPeriodEntity {
    return plainToInstance(ActivityPeriodEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ActivityPeriod> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ActivityPeriodValidator, object);
  }

  asTypeValue(): ActivityPeriod {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ActivityPeriodValidator, object);
  }
}

export type ActivityPeriodSearchCriteria = {
  fromDttm?: number;
  toDttm?: number;
  regionIds?: string[];
  operatorIds?: string[];
  operatorUnitIds?: string[]
  truckCompanyIds?: string[];
  categories?: string[];
  types?: string[]
};
